/** Figma Atlantic formatters for LIDECO Semáforo — keep free of React for unit tests. */

import { formatAbsoluteLastExecution } from './lastExecutionDisplay';

/** Figma Atlantic: amounts as 125.000.983,20 */
export const formatLidecoAmount = (raw?: string | null): string => {
  if (raw == null || raw === '' || raw === '—') return '—';
  const s = String(raw).trim().replace(/\s/g, '');
  let normalized = s;
  if (s.includes(',') && s.includes('.')) {
    // already ES-like 125.000.983,20
    normalized = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    normalized = s.replace(',', '.');
  }
  const n = Number(normalized);
  if (Number.isNaN(n)) return String(raw);
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** "CALIF: 1C" | "5" → display token for badge */
export const extractClassificationCode = (raw?: string | null): string => {
  if (!raw) return '—';
  const m =
    String(raw).match(/CALIF:\s*([0-9A-Za-z]+)/i) || String(raw).match(/^\s*([0-9A-Za-z]+)\s*$/);
  return m?.[1] || String(raw).trim();
};

/** BCU calificación → detalle cuando el backend no envía descripción (LIDECO_004). */
export const BANK_CLASSIFICATION_DETAIL_DEFAULTS: Readonly<Record<string, string>> = {
  '1A': 'Operaciones con garantías autoliquidables admitidas',
  '1C': 'Deudores con capacidad de pago fuerte',
  '2A': 'Deudores con capacidad de pago adecuada',
  '2B': 'Deudores con capacidad de pago con problemas potenciales',
  '3': 'Deudores con capacidad de pago comprometida',
  '4': 'Deudores con capacidad de pago muy comprometida',
  '5': 'Deudores irrecuperables',
};

const LOW_CLASSIFICATION_CODES = new Set(['1A', '1C', '2A', '2B']);
const MEDIUM_CLASSIFICATION_CODES = new Set(['3']);
const HIGH_CLASSIFICATION_CODES = new Set(['4', '5']);

export const defaultBankClassificationDetail = (classification?: string | null): string | undefined => {
  const code = extractClassificationCode(classification);
  if (!code || code === '—') return undefined;
  return BANK_CLASSIFICATION_DETAIL_DEFAULTS[code.toUpperCase()];
};

export const classificationVariant = (
  code: string
): 'success' | 'warning' | 'danger' | 'neutral' => {
  const c = extractClassificationCode(code).toUpperCase();
  if (c === '—') return 'neutral';
  if (LOW_CLASSIFICATION_CODES.has(c)) return 'success';
  if (MEDIUM_CLASSIFICATION_CODES.has(c)) return 'warning';
  if (HIGH_CLASSIFICATION_CODES.has(c)) return 'danger';
  return 'neutral';
};

export const formatLidecoLastQuery = (raw?: string | null): string =>
  formatAbsoluteLastExecution(raw);

export const formatLidecoRelativeQuery = formatLidecoLastQuery;

export const isTotalConcept = (label: string) => /^total\b/i.test(label.trim());

/** Accept JSON strings, double-encoded strings, or already-parsed objects from API. */
export const parseIndicatorJson = <T,>(raw?: unknown): T | null => {
  if (raw == null || raw === 'null') return null;
  if (typeof raw === 'object') return raw as T;
  if (typeof raw !== 'string') return null;
  try {
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }
    return parsed as T;
  } catch {
    return null;
  }
};

const asArray = (value: unknown): unknown[] | null =>
  Array.isArray(value) ? value : null;

const pickString = (row: Record<string, unknown>, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const raw = row[key];
    if (raw == null || raw === '') continue;
    return String(raw);
  }
  return undefined;
};

/** True when a row carries at least one displayable scalar (not an empty object). */
export const isUsableNegativeRecord = (item: unknown): boolean => {
  if (item == null) return false;
  if (typeof item === 'string') return item.trim() !== '';
  if (Array.isArray(item)) return item.some((v) => v != null && String(v).trim() !== '');
  if (typeof item !== 'object') return String(item).trim() !== '';
  return Object.values(item as Record<string, unknown>).some(
    (v) => v != null && String(v).trim() !== ''
  );
};

const sectionItemCandidates = (block?: Record<string, unknown> | null): unknown[][] => {
  if (!block) return [];
  return [
    block.items,
    block.registros,
    block.instituciones,
    block.procesos_ejecutivos,
  ]
    .map(asArray)
    .filter((arr): arr is unknown[] => Array.isArray(arr));
};

/** Prefer registros/instituciones when items is only placeholders (live LIDECO / ms-risk-analysis). */
export const negativeSectionItems = (block?: Record<string, unknown> | null): unknown[] => {
  const candidates = sectionItemCandidates(block);
  const usable = candidates.find((arr) => arr.some(isUsableNegativeRecord));
  if (usable) return usable;
  return candidates.find((arr) => arr.length > 0) ?? [];
};

export const negativeSectionHasUsableItems = (block?: Record<string, unknown> | null): boolean =>
  negativeSectionItems(block).some(isUsableNegativeRecord);

/** Pick the section variant that actually has row data (flat vs nested live shapes). */
export const pickBestNegativeSection = (
  ...sections: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> | undefined => {
  const defined = sections.filter(Boolean) as Record<string, unknown>[];
  return (
    defined.find((section) => negativeSectionHasUsableItems(section)) ??
    defined.find((section) => negativeSectionItems(section).length > 0) ??
    defined[0]
  );
};

const mergeSectionIfEmpty = (
  target: Record<string, unknown>,
  key: string,
  nested?: Record<string, unknown>
) => {
  if (!nested) return;
  const current = target[key] as Record<string, unknown> | undefined;
  if (!negativeSectionHasUsableItems(current)) {
    target[key] = nested;
  }
};

/** ms-risk-analysis LIDECO_006: flat negatives or nested antecedentes_negativos wrappers. */
export const normalizeNegativeBackground = (
  raw: unknown
): Record<string, unknown> | null => {
  const parsed = parseIndicatorJson<unknown>(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const nested =
    obj.antecedentes_negativos ??
    obj.negativeBackground ??
    obj.negative_background;
  const base = ({ ...(nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : obj) }) as Record<
    string,
    unknown
  >;

  const sancionadas = base.cuentas_corrientes_bancarias_sancionadas as
    | Record<string, unknown>
    | undefined;
  if (sancionadas) {
    mergeSectionIfEmpty(base, 'vigentes', sancionadas.vigentes as Record<string, unknown> | undefined);
    mergeSectionIfEmpty(base, 'no_vigentes', sancionadas.no_vigentes as Record<string, unknown> | undefined);
  }

  mergeSectionIfEmpty(
    base,
    'morosos_y_castigados',
    base.morosos_y_castigados_sistema_financiero as Record<string, unknown> | undefined
  );

  return base;
};

/** ms-risk-analysis LIDECO_004 may be a bare array or wrapped in calificaciones/bankRatings. */
export const normalizeBankRatings = (raw: unknown): Array<Record<string, unknown>> => {
  const parsed = parseIndicatorJson<unknown>(raw);
  if (!parsed) return [];
  const direct = asArray(parsed);
  if (direct) return direct as Array<Record<string, unknown>>;
  if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    const nested =
      asArray(obj.calificaciones) ??
      asArray(obj.bankRatings) ??
      asArray(obj.bank_ratings);
    return (nested ?? []) as Array<Record<string, unknown>>;
  }
  return [];
};

/** ms-risk-analysis LIDECO_005 may be a bare array or wrapped in resumen_total/totalSummary. */
export const normalizeSummaryRows = (raw: unknown): Array<Record<string, unknown>> => {
  const parsed = parseIndicatorJson<unknown>(raw);
  if (!parsed) return [];
  const direct = asArray(parsed);
  if (direct) return direct as Array<Record<string, unknown>>;
  if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    const nested =
      asArray(obj.resumen_total) ??
      asArray(obj.totalSummary) ??
      asArray(obj.total_summary);
    return (nested ?? []) as Array<Record<string, unknown>>;
  }
  return [];
};

export const bankInstitutionLabel = (bank: Record<string, unknown>): string => {
  const raw =
    bank.institutionName ??
    bank.nombre_institucion ??
    bank.institucion ??
    bank.nombreInstitucion;
  if (raw == null || raw === '') return '—';
  return String(raw);
};

export const bankClassificationLabel = (bank: Record<string, unknown>): string | undefined => {
  const raw =
    bank.classification ??
    bank.clasificacion ??
    bank.classification_code ??
    bank.codigo_clasificacion;
  return raw == null || raw === '' ? undefined : String(raw);
};

export const bankDetailLabel = (bank: Record<string, unknown>): string => {
  const raw = bank.detail ?? bank.detalle ?? bank.descripcion;
  if (raw != null && raw !== '') return String(raw);

  const fallback = defaultBankClassificationDetail(bankClassificationLabel(bank));
  return fallback ?? '—';
};

export const morosoInstitutionLabel = (item: unknown): string => {
  if (typeof item === 'string') return item;
  if (Array.isArray(item)) {
    const first = item.find((v) => v != null && String(v).trim() !== '');
    return first == null ? '—' : String(first);
  }
  if (!item || typeof item !== 'object') return '—';
  const row = item as Record<string, unknown>;
  return (
    pickString(
      row,
      'institution',
      'institucion',
      'nombre_institucion',
      'nombreInstitucion',
      'nombre',
      'description',
      'descripcion',
      'detalle',
      'detail'
    ) ?? '—'
  );
};

export const sanctionRowLabel = (
  item: unknown,
  field: 'sanction' | 'from' | 'to' | 'validity' | 'institution'
): string => {
  if (Array.isArray(item)) {
    const idx =
      field === 'sanction'
        ? 0
        : field === 'from'
          ? 1
          : field === 'to'
            ? 2
            : field === 'validity'
              ? 3
              : 4;
    const raw = item[idx];
    return raw == null || raw === '' ? '—' : String(raw);
  }
  if (!item || typeof item !== 'object') return '—';
  const row = item as Record<string, unknown>;
  const keysByField: Record<typeof field, string[]> = {
    sanction: ['sanction', 'sancion', 'sancion_tipo', 'tipo_sancion', 'tipo_descripcion', 'tipo'],
    from: ['from', 'desde', 'fecha_desde', 'fecha_inicio'],
    to: ['to', 'hasta', 'fecha_hasta', 'fecha_fin'],
    validity: ['validity', 'plazo', 'vigencia', 'periodo', 'duracion'],
    institution: [
      'institution',
      'institucion',
      'nombre_institucion',
      'nombreInstitucion',
      'nombre',
      'banco',
    ],
  };
  return pickString(row, ...keysByField[field]) ?? '—';
};

export const listDetailLabel = (item: unknown): string => {
  if (typeof item === 'string') return item;
  if (Array.isArray(item)) {
    const raw = item[0];
    return raw == null || raw === '' ? '—' : String(raw);
  }
  if (!item || typeof item !== 'object') return '—';
  const row = item as Record<string, unknown>;
  return (
    pickString(
      row,
      'description',
      'descripcion',
      'detail',
      'detalle',
      'institution',
      'institucion',
      'nombre',
      'nombre_institucion'
    ) ?? '—'
  );
};

export const listDateLabel = (item: unknown): string => {
  if (Array.isArray(item)) {
    const raw = item[1] ?? item[item.length - 1];
    return raw == null || raw === '' ? '—' : String(raw);
  }
  if (!item || typeof item !== 'object') return '—';
  const row = item as Record<string, unknown>;
  return (
    pickString(
      row,
      'date',
      'fecha',
      'fecha_presentacion',
      'fechaPresentacion'
    ) ?? '—'
  );
};
